/**
 * LINE 系コレクションの CRUD hooks。
 * 仕様書: docs/01_spec/spec_line_bot.md A-5
 */
import type { ContentDelivery, Event, LineUser, Offering } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';


import { db, functions } from '@/lib/firebase';
import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

const generateDeliveryCodeFn = httpsCallable<Record<string, never>, { code: string }>(
  functions,
  'generateUniqueDeliveryCode',
);

// ─────────────────────────────────────────────
// Events
// ─────────────────────────────────────────────
export interface EventRecord extends Event {
  id: string;
}

export function useEvents(limitCount = 100) {
  return useQuery({
    queryKey: ['events', limitCount],
    queryFn: async (): Promise<EventRecord[]> => {
      const q = query(
        collection(db, 'events'),
        orderBy('eventDate', 'desc'),
        limitFn(limitCount),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Event) }));
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Event>) => {
      await safeAddDoc(collection(db, 'events'), {
        ...data,
        currentAttendees: data.currentAttendees ?? 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<Event> }) => {
      await safeUpdateDoc(doc(db, 'events', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'events', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

/**
 * イベント複製。参加実数・参加者コード・公開状態はリセットする。
 * 仕様書: docs/01_spec/spec_line_bot.md A-5 / 複製機能
 */
export function useDuplicateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (originalId: string): Promise<string> => {
      const snap = await getDoc(doc(db, 'events', originalId));
      if (!snap.exists()) throw new Error('複製元のイベントが見つかりません');
      const original = snap.data() as Event;
      const ref = await safeAddDoc(collection(db, 'events'), {
        name: `(コピー) ${original.name ?? ''}`,
        eventDate: original.eventDate ?? null,
        capacity: original.capacity ?? 0,
        currentAttendees: 0,
        attendanceBreakdown: { onsite: 0, online: 0, archive: 0 },
        tag: original.tag ?? 'online',
        zoomLink: original.zoomLink ?? null,
        zoomId: original.zoomId ?? null,
        zoomPassword: original.zoomPassword ?? null,
        publishedAt: null,
        overview: original.overview ?? '',
        recommendedFor: original.recommendedFor ?? '',
        participantBenefits: original.participantBenefits ?? '',
        participantCode: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

// ─────────────────────────────────────────────
// Content Deliveries
// ─────────────────────────────────────────────
export interface ContentDeliveryRecord extends ContentDelivery {
  id: string;
}

export function useContentDeliveries(limitCount = 100) {
  return useQuery({
    queryKey: ['content_deliveries', limitCount],
    queryFn: async (): Promise<ContentDeliveryRecord[]> => {
      const q = query(
        collection(db, 'content_deliveries'),
        orderBy('endAt', 'desc'),
        limitFn(limitCount),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as ContentDelivery) }));
    },
  });
}

/**
 * 配信コードの重複事前チェック（UI フィードバック用）。
 * 真の一意性は Cloud Function 側 Transaction で担保する。
 */
export async function checkDeliveryCodeAvailability(
  code: string,
  excludeId?: string,
): Promise<{ available: boolean; conflictStatus?: string }> {
  const normalized = code.trim().toUpperCase();
  if (!/^[A-Z0-9]{6,20}$/.test(normalized)) {
    return { available: false };
  }
  const q = query(
    collection(db, 'content_deliveries'),
    where('deliveryCode', '==', normalized),
  );
  const snap = await getDocs(q);
  const conflict = snap.docs.find((d) => {
    if (d.id === excludeId) return false;
    const status = (d.data().status ?? 'expired') as string;
    return status !== 'expired';
  });
  return {
    available: !conflict,
    conflictStatus: conflict?.data().status as string | undefined,
  };
}

export function useDeleteContentDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'content_deliveries', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content_deliveries'] }),
  });
}

/**
 * 配信を複製。deliveryCode はサーバー側で衝突しない新コードを発番、
 * アクセス数・旧コード履歴・status はリセットする。
 */
export function useDuplicateContentDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (originalId: string): Promise<string> => {
      const snap = await getDoc(doc(db, 'content_deliveries', originalId));
      if (!snap.exists()) throw new Error('複製元の配信が見つかりません');
      const original = snap.data() as ContentDelivery;
      const res = await generateDeliveryCodeFn({});
      const ref = await safeAddDoc(collection(db, 'content_deliveries'), {
        contentLink: original.contentLink ?? '',
        deliveryCode: res.data.code,
        previousCodes: [],
        contentType: original.contentType ?? 'archive_playback',
        startAt: original.startAt ?? null,
        endAt: original.endAt ?? null,
        status: 'scheduled',
        accessCount: 0,
        password: original.password ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content_deliveries'] }),
  });
}

// ─────────────────────────────────────────────
// Offerings (service / product)
// ─────────────────────────────────────────────
export interface OfferingRecord extends Offering {
  id: string;
}

export function useOfferings(tag?: 'service' | 'product', limitCount = 100) {
  return useQuery({
    queryKey: ['offerings', tag, limitCount],
    queryFn: async (): Promise<OfferingRecord[]> => {
      const baseQ = tag
        ? query(
            collection(db, 'offerings'),
            where('tag', '==', tag),
            orderBy('updatedAt', 'desc'),
            limitFn(limitCount),
          )
        : query(collection(db, 'offerings'), orderBy('updatedAt', 'desc'), limitFn(limitCount));
      const snap = await getDocs(baseQ);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Offering) }));
    },
  });
}

export function useCreateOffering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Offering>) => {
      await safeAddDoc(collection(db, 'offerings'), {
        ...data,
        pdfFiles: data.pdfFiles ?? [],
        recommendedFor: data.recommendedFor ?? '',
        faq: data.faq ?? [],
        embedding: data.embedding ?? [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offerings'] }),
  });
}

export function useUpdateOffering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<Offering> }) => {
      await safeUpdateDoc(doc(db, 'offerings', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offerings'] }),
  });
}

export function useDeleteOffering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'offerings', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offerings'] }),
  });
}

/**
 * 商材（サービス / 製品）の複製。
 * pdfFiles は Cloud Storage パスが offeringId に紐付くため空配列で開始する
 * （複製先で再アップロードしてもらう）。embedding も RAG 再生成に委ねるためリセット。
 */
export function useDuplicateOffering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (originalId: string): Promise<string> => {
      const snap = await getDoc(doc(db, 'offerings', originalId));
      if (!snap.exists()) throw new Error('複製元の商材が見つかりません');
      const original = snap.data() as Offering;
      const ref = await safeAddDoc(collection(db, 'offerings'), {
        name: `(コピー) ${original.name ?? ''}`,
        tag: original.tag,
        pdfFiles: [],
        descriptionText: original.descriptionText ?? '',
        recommendedFor: original.recommendedFor ?? '',
        websiteUrl: original.websiteUrl ?? null,
        faq: original.faq ?? [],
        embedding: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offerings'] }),
  });
}

// ─────────────────────────────────────────────
// Users / Sessions
// ─────────────────────────────────────────────
export interface UserRecord extends LineUser {
  id: string; // == lineUid
}

export function useLineUsers(limitCount = 50) {
  return useQuery({
    queryKey: ['line_users', limitCount],
    queryFn: async (): Promise<UserRecord[]> => {
      const q = query(
        collection(db, 'users'),
        orderBy('lastMessageAt', 'desc'),
        limitFn(limitCount),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as LineUser) }));
    },
  });
}

/**
 * 指定 lineUid のリストから users ドキュメントを並列取得（Phase 24g）。
 * ID が同じ users コレクションのドキュメント ID なので getDoc 並列で OK。
 * `users` に存在しない uid は { id, lineUid, displayName: '(未登録)', ... } でプレースホルダ返却。
 */
export function useLineUsersByIds(uids: string[]) {
  // クエリキーは安定化のためソート
  const stableKey = [...uids].sort().join(',');
  return useQuery({
    queryKey: ['line_users_by_ids', stableKey],
    enabled: uids.length > 0,
    queryFn: async (): Promise<UserRecord[]> => {
      if (uids.length === 0) return [];
      const refs = uids.map((uid) => doc(db, 'users', uid));
      const snaps = await Promise.all(refs.map((r) => getDoc(r)));
      return snaps.map((snap, idx) => {
        const uid = uids[idx];
        if (snap.exists()) {
          return { id: snap.id, ...(snap.data() as LineUser) };
        }
        return {
          id: uid,
          lineUid: uid,
          displayName: '(未登録)',
          conversationActive: false,
          currentSessionId: null,
          currentInquiryType: null,
          lastMessageAt: '',
          createdAt: '',
          updatedAt: '',
        };
      });
    },
  });
}

export function useUserSessions(lineUid: string | null, limitCount = 20) {
  return useQuery({
    queryKey: ['sessions', lineUid, limitCount],
    enabled: Boolean(lineUid),
    queryFn: async () => {
      if (!lineUid) return [];
      const q = query(
        collection(db, 'users', lineUid, 'sessions'),
        orderBy('startedAt', 'desc'),
        limitFn(limitCount),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
  });
}

export function useSessionMessages(
  lineUid: string | null,
  sessionId: string | null,
) {
  return useQuery({
    queryKey: ['messages', lineUid, sessionId],
    enabled: Boolean(lineUid && sessionId),
    queryFn: async () => {
      if (!lineUid || !sessionId) return [];
      const q = query(
        collection(db, 'users', lineUid, 'sessions', sessionId, 'messages'),
        orderBy('timestamp', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
  });
}

export async function getUserDoc(lineUid: string): Promise<LineUser | null> {
  const snap = await getDoc(doc(db, 'users', lineUid));
  return snap.exists() ? (snap.data() as LineUser) : null;
}
